import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { liveQuery } from "dexie";
import { observer } from "mobx-react-lite";
import {
  MDSActionMenuWithContent,
  MDSDialogSlotName,
  MDSMenuItemSelectionVariant,
  MDSModalDialog,
  MDSSession,
  MDSText,
  MDSTextVariant,
} from "@/mds";
import type {
  FactSourceKind,
  IMDSSessionRef,
  ResolvedFactSource,
} from "@/mds/components/sessions/session.types";
import { SessionFeature, SessionStatus } from "@/mds/components/sessions/session.types";
import { exportSessionEvents } from "./classic-chat-session-events";
import { getBackendAssetProxyUrl } from "@/manager/backend-api-client/initializeBackendApiManager";
import type { MemCommonEditorAssetProxyConfig } from "@mem-labs/common-editor";
import type { SessionEventModelData } from "@/store/sessions/types";
import { useAppStore } from "@/store";

const READ_ONLY_SESSION_HEIGHT_BASIS = "calc(100% + 58px)";

export const ClassicChatModal = observer(function ClassicChatModal() {
  const { pageStore } = useAppStore();
  const classicChatHistoryStore = pageStore.classicChatHistory;
  const isOpen = classicChatHistoryStore.isHistoryModalOpen;
  const previousChatSessions = classicChatHistoryStore.previousChatSessions;
  const firstSessionId = previousChatSessions[0]?.modelId;
  const selectedSessionId = classicChatHistoryStore.selectedSessionId ?? firstSessionId;

  useEffect(() => {
    if (!isOpen) return;
    if (!firstSessionId) return;

    const selectedSessionStillExists = previousChatSessions.some(
      session => session.modelId === selectedSessionId
    );
    if (!selectedSessionId || !selectedSessionStillExists) {
      classicChatHistoryStore.setSelectedSessionId(firstSessionId);
    }
  }, [classicChatHistoryStore, firstSessionId, isOpen, previousChatSessions, selectedSessionId]);

  const contentList = classicChatHistoryStore.buildPreviousSessionsContentList({
    onSelectSession: classicChatHistoryStore.setSelectedSessionId,
    selectedSessionId,
    selectionVariant: MDSMenuItemSelectionVariant.Hover,
  });

  const selectedSession = previousChatSessions.find(
    session => session.modelId === selectedSessionId
  );

  return (
    <StyledModalDialog
      isOpen={isOpen}
      handleClose={classicChatHistoryStore.closeHistoryModal}
      fluidWidth
    >
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        Classic Chat History
      </MDSText>
      <Layout slot={MDSDialogSlotName.Content}>
        <SessionsColumn>
          <SessionListMenu contentList={contentList} />
        </SessionsColumn>
        <HistoryColumn>
          {selectedSessionId && selectedSession && (
            <ClassicChatSessionHistory
              key={selectedSessionId}
              sessionId={selectedSessionId}
              title={selectedSession.title}
            />
          )}
        </HistoryColumn>
      </Layout>
    </StyledModalDialog>
  );
});

const ClassicChatSessionHistory = observer(function ClassicChatSessionHistory({
  sessionId,
  title,
}: {
  sessionId: string;
  title: string;
}) {
  const { store } = useAppStore();
  const sessionRef = useRef<IMDSSessionRef>(null);
  const [sessionEvents, setSessionEvents] = useState<SessionEventModelData[]>([]);
  const sessionObservable = store.sessions.get(sessionId);
  const sessionStatus =
    (sessionObservable?.modelData.status as SessionStatus) ?? SessionStatus.Finished;

  useEffect(() => {
    const subscription = liveQuery(() =>
      store.sessionEvents.getManySessionEventsBySessionId(sessionId)
    ).subscribe({
      next: setSessionEvents,
      error: () => setSessionEvents([]),
    });

    return () => subscription.unsubscribe();
  }, [sessionId, store.sessionEvents]);

  const exportedEvents = useMemo(() => exportSessionEvents(sessionEvents), [sessionEvents]);

  useEffect(() => {
    sessionRef.current?.setCurrentSessionId(sessionId);
    sessionRef.current?.upsertEvents(exportedEvents);
  }, [exportedEvents, sessionId]);

  useEffect(() => {
    sessionRef.current?.updateStatus(sessionStatus);
  }, [sessionStatus]);

  const resolveFactSource = useCallback(
    async (kind: FactSourceKind, id: string): Promise<ResolvedFactSource | null> => {
      if (kind === "NOTE") {
        const noteObservable = await store.notes.getAsync(id);
        if (!noteObservable) return null;

        return {
          id,
          label: noteObservable.title,
          kind: "NOTE",
        };
      }

      if (kind === "COLLECTION") {
        const collectionObservable = await store.collections.getAsync(id);
        if (!collectionObservable) return null;

        return {
          id,
          label: collectionObservable.label,
          kind: "COLLECTION",
        };
      }

      return null;
    },
    [store.collections, store.notes]
  );

  const resolveAssetProxy = useCallback(async (): Promise<
    MemCommonEditorAssetProxyConfig | undefined
  > => {
    const authStore = store.publicAppStore.auth;
    if (!authStore.isStandardMode) return undefined;

    const token = await authStore.assetAccessTokenState.ensureToken();
    if (!token) return undefined;

    const expiresAtIso = authStore.assetAccessTokenState.expiresAt
      ? authStore.assetAccessTokenState.expiresAt.toUTC().toISO()
      : null;

    return {
      baseUrl: getBackendAssetProxyUrl(),
      auth: { assetAccessToken: token, expiresAt: expiresAtIso },
    };
  }, [store.publicAppStore.auth]);

  return (
    <HistoryWrapper>
      <ReadOnlySession>
        <MDSSession
          ref={sessionRef}
          sessionTitle={title}
          sessionSubtitle={`Session: ${sessionId}`}
          initialSessionEvents={exportedEvents}
          initialSessionStatus={sessionStatus}
          containerHeight={400}
          isOpen
          config={{
            features: [SessionFeature.AllowCopy],
            openLinksInNewTab: true,
            resolveFactSource,
            resolveAssetProxy,
            disableListEnterAnimation: true,
            initialScrollPosition: "top",
            staticViewChromeHeightPx: 0,
            staticViewHeightBasis: READ_ONLY_SESSION_HEIGHT_BASIS,
          }}
          handlers={{
            handleFetchComboboxData: async () => ({ data: [] }),
            handleInputFormSubmit: async () => false,
            handleViewModeChanged: () => {},
            handleCountdownCompleted: () => {},
          }}
        />
      </ReadOnlySession>
    </HistoryWrapper>
  );
});

const StyledModalDialog = styled(MDSModalDialog)`
  .mds-dialog {
    width: min(960px, 92vw);
    height: min(720px, 88dvh);
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 280px) minmax(420px, 1fr);
  height: min(600px, calc(88dvh - 104px));
  min-height: 360px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background-color: ${({ theme }) => theme.colors.surface.pane};

  ${({ theme }) => theme.mediaQueries.rules.phone} {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(140px, 38%) minmax(0, 1fr);
    min-height: 0;
  }
`;

const SessionsColumn = styled.div`
  min-width: 0;
  overflow-y: auto;
  border-right: 1px solid ${({ theme }) => theme.colors.border.surface};
  background-color: ${({ theme }) => theme.colors.surface.menu};

  ${({ theme }) => theme.mediaQueries.rules.phone} {
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.surface};
  }
`;

const SessionListMenu = styled(MDSActionMenuWithContent)`
  width: 100%;
  max-width: none;
  padding-block: ${({ theme }) => theme.spacing[3]};
  box-shadow: none;
  border: none;
  border-radius: 0;

  @media (hover: hover) and (pointer: fine) {
    [data-classic-chat-history-delete-action] {
      opacity: 0;
      pointer-events: none;
    }

    [data-mds-name="mds-menu-item"]:hover [data-classic-chat-history-delete-action],
    [data-mds-name="mds-menu-item"]:focus-within [data-classic-chat-history-delete-action],
    [data-focused] [data-mds-name="mds-menu-item"] [data-classic-chat-history-delete-action],
    [data-classic-chat-history-delete-action]:focus-visible {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const HistoryColumn = styled.div`
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.surface.chat};
`;

const HistoryWrapper = styled.div`
  height: 100%;
  min-height: 0;
`;

const ReadOnlySession = styled.div`
  height: 100%;
  min-height: 0;
  overflow: hidden;

  [data-component="StyledListContainer"],
  [data-mds-name="MDSSessionList"],
  [data-component="StyledSessionBody"] {
    height: 100%;
    min-height: 0;
  }

  [data-component="StyledSection"] {
    display: none;
  }
`;
